/**
 * 2026-09-22 — Operasyon kokpiti (Genel Bakış) veri katmanı.
 *
 * Three calls, all typed against the shared contracts of this round:
 *   GET  /admin/overview                                   (provider:view_health)
 *        one bounded server aggregate: system strip, gateways, per-provider
 *        health and the server-ranked problem list. The server owns every
 *        status rule; this module only validates + normalises the payload.
 *   GET  /admin/overview/alarms                            (provider:view_health)
 *        cached evaluator state (Redis) for the global alarm banner and the
 *        bell; since 2026-09-28 (D-243) every alarm is an INCIDENT with the
 *        operator's marks (Okundu / İncelendi / Ertele).
 *   POST /admin/overview/incidents/:id/{acknowledge,review,snooze}
 *        (provider:inspect) the audited incident marks (D-243).
 *   POST /admin/providers/:providerId/parents/retry-problematic
 *        (provider:manage_inventory, mailbox-unit providers only). The SERVER
 *        picks the candidates; `confirm !== true` is an audited dry run.
 *
 * Fail loud, never silently stale: a payload that does not look like the
 * contract (missing generatedAt/providers, or the SPA's index.html answering an
 * un-routed XHR) is surfaced as an error instead of an empty "Sorun yok" page.
 */
import { callAdminApi, type ApiClientOptions, type ApiResult } from './client.js';

export type OverviewSeverity = 'critical' | 'warn' | 'info';
export type ProviderHealthStatus = 'ok' | 'warn' | 'critical' | 'idle';
export type GatewayHealthStatus = 'ok' | 'down' | 'unknown';
export type ResidentialStatus = 'ok' | 'down' | 'unknown' | 'unconfigured';
export type GatewaySignalSource = 'canary' | 'probes' | 'none';
export type ProviderKind = 'alias' | 'mailbox';

export type ProblemCode =
  | 'gateway_down'
  | 'residential_gateway_unconfigured'
  | 'runtime_metrics_stale'
  | 'metrics_unavailable'
  | 'provider_no_delivery'
  | 'provider_sellable_zero'
  | 'provider_stale_verification'
  | 'provider_sticky_quarantine'
  | 'provider_stock_gap'
  | 'stuck_orders'
  | 'notifier_unconfigured'
  | 'evaluator_silent'
  // 2026-09-23 — display-only overview problems (never alarm e-mails).
  //   provider_low_delivery      {delivered, finished, ratePct}
  //   site_low_delivery          {site, delivered, finished, ratePct}
  //   customer_low_delivery      {actorId, email, delivered, finished, ratePct, topSite}
  //   provider_login_failed      {count, total}
  //   provider_disabled_accounts {count, total}
  //   provider_low_stock         {sellable}
  //   provider_renewal_failing   {failed, completed}
  //   notifier_not_configured    {}
  | 'provider_low_delivery'
  | 'site_low_delivery'
  | 'customer_low_delivery'
  | 'provider_login_failed'
  | 'provider_disabled_accounts'
  | 'provider_low_stock'
  | 'provider_renewal_failing'
  | 'notifier_not_configured'
  // 2026-09-23 — PAGING delivery-incident codes (server ALARM_CODES; they also
  // appear in GET /admin/overview/alarms). D-236: alarm + suggestion, never a block.
  //   site_family_no_delivery   {site, domain, provider, rule: 'rate_1h'|'streak', delivered, finished,
  //                              ratePct, streak, delivered24h, finished24h,
  //                              classification: 'site_side'|'pipeline', controlFinished, controlDelivered,
  //                              bestAlternativeDomain, bestAlternativeRatePct}
  //   gmx_imap_bad_sellable     {parents, aliases, sold (parents allocated after their rejection)}
  //   customer_zero_yield_loop  {actorId, email (null for admins without user capabilities;
  //                              absent in alarms), site, count, domain}
  //   inbox_listing_failing     {failed, total, failPct}
  //   sender_skip_high          {site, skips, delivered, expired}
  | 'site_family_no_delivery'
  | 'gmx_imap_bad_sellable'
  | 'customer_zero_yield_loop'
  | 'inbox_listing_failing'
  | 'sender_skip_high'
  // 2026-09-28 (D-243) — site_family_no_delivery / sender_skip_high /
  // customer_zero_yield_loop / site & customer low delivery are INFORMATIONAL
  // now (severity 'info'; never an alarm). New:
  //   provider_no_mail_1h  {orders, sites, customers, windowMinutes} — PAGING
  //   domain_stock_out     {domain, reservable, orders24h, peakHour24h, coverHours} — PAGING
  //   domain_low_stock     {domain, reservable, orders24h, peakHour24h, coverHours} — display only
  //   provider_renewal_stuck {parents, sessionNotActive, inCooldown, minutes} — WARN incident, never mailed
  //                          (D-240 step 8: exhausted mail.com / GMX parents that are not renewing)
  | 'provider_no_mail_1h'
  | 'domain_stock_out'
  | 'domain_low_stock'
  | 'provider_renewal_stuck';

export type ProblemParams = Readonly<Record<string, string | number | null>>;

export interface OverviewOrders24h {
  readonly total: number;
  readonly delivered: number;
  readonly cancelled: number;
  readonly open: number;
}

export interface OverviewOrderDay {
  /** UTC day, YYYY-MM-DD. */
  readonly day: string;
  readonly total: number;
  readonly delivered: number;
  readonly cancelled: number;
}

/** 2026-09-23 — one recipient domain of a (site, provider family) row, 24 h (+ last hour). */
export interface OverviewSiteDomainDelivery {
  readonly domain: string;
  readonly finished: number;
  readonly delivered: number;
  readonly ratePct: number;
  readonly finished1h: number;
  readonly delivered1h: number;
}

/** 2026-09-23 — delivery per site × provider family over 24 h (next to the per-day figure). */
export interface OverviewSiteFamilyDelivery {
  readonly site: string;
  readonly providerId: string;
  readonly finished: number;
  readonly delivered: number;
  readonly ratePct: number;
  readonly domains: readonly OverviewSiteDomainDelivery[];
}

export interface OverviewSystem {
  readonly orders24h: OverviewOrders24h;
  readonly ordersDaily7d: readonly OverviewOrderDay[];
  readonly stuckOrders: { readonly expiredLease: number; readonly oldReserved: number };
  readonly residential: { readonly configured: boolean | null; readonly host: string | null; readonly port: number | null; readonly status: ResidentialStatus };
  readonly runtime: { readonly metricsFresh: boolean | null };
  readonly notifier: { readonly emailConfigured: boolean };
  readonly metricsAvailable: boolean;
  /** Additive (2026-09-23); [] from an older server or when its query failed. */
  readonly deliveryBySiteFamily24h: readonly OverviewSiteFamilyDelivery[];
  /** Additive (2026-09-28, D-243): on-sale domains OUT of / LOW on reservable stock, worst first. */
  readonly stockByDomain: readonly OverviewDomainStock[];
  /** false = the per-domain stock source failed or the server is older: unknown, never "all fine". */
  readonly stockByDomainKnown: boolean;
  readonly stockDomainsWithDemand: number;
  /** On-sale domains without demand holding little stock (vanity domains; counted only). */
  readonly stockIdleLowCount: number;
}

/** 2026-09-28 (D-243) — one on-sale domain that is out of / low on reservable stock. */
export interface OverviewDomainStock {
  readonly providerId: string;
  readonly domain: string;
  readonly reservable: number;
  readonly orders24h: number;
  readonly peakHour24h: number;
  /** Hours of cover at max(24 h average, half the peak hour); null = no rate. */
  readonly coverHours: number | null;
  readonly status: 'out' | 'low';
}

export interface OverviewGateway {
  readonly providerIds: readonly string[];
  readonly host: string;
  readonly port: number;
  readonly status: GatewayHealthStatus;
  readonly since: string | null;
  readonly lastOkAt: string | null;
  readonly source: GatewaySignalSource;
}

export interface OverviewReasonCount {
  readonly code: string;
  readonly count: number;
}

export interface OverviewProviderAccounts {
  readonly total: number;
  readonly active: number;
  readonly verified: number;
  readonly pending: number;
  readonly loginFailed: number;
  readonly sticky: number;
  readonly disabled: number;
  readonly suspended: number;
  readonly noCredential: number;
  readonly lastVerifiedAt: string | null;
}

export interface OverviewProvider {
  readonly providerId: string;
  readonly label: string;
  readonly kind: ProviderKind;
  readonly onSale: boolean;
  readonly activeDomains: number;
  readonly accounts: OverviewProviderAccounts;
  readonly quarantineReasons: readonly OverviewReasonCount[];
  readonly stock: {
    readonly displayed: number;
    readonly sellable: number;
    readonly rawAvailable: number;
    readonly inFlight: number;
  };
  readonly orders24h: { readonly total: number; readonly delivered: number; readonly cancelled: number };
  readonly orders7d: { readonly total: number; readonly delivered: number };
  readonly lastDeliveryAt: string | null;
  readonly renewal24h: { readonly completed: number; readonly failed: number } | null;
  readonly gateway: { readonly status: GatewayHealthStatus; readonly since: string | null } | null;
  readonly status: ProviderHealthStatus;
}

export type OverviewProblemAction =
  | { readonly kind: 'retry_problematic'; readonly providerId: string }
  | { readonly kind: 'link'; readonly href: string }
  | null;

export interface OverviewProblem {
  readonly id: string;
  readonly severity: OverviewSeverity;
  /** A {@link ProblemCode}; kept as string so an unknown future code still renders. */
  readonly code: string;
  readonly providerId: string | null;
  readonly params: ProblemParams;
  readonly since: string | null;
  readonly action: OverviewProblemAction;
}

export interface OverviewResponse {
  readonly generatedAt: string;
  readonly cacheAgeSec: number;
  readonly system: OverviewSystem;
  readonly gateways: readonly OverviewGateway[];
  readonly providers: readonly OverviewProvider[];
  readonly problems: readonly OverviewProblem[];
}

export interface OverviewAlarm {
  readonly id: string;
  readonly severity: 'critical' | 'warn';
  readonly code: string;
  readonly providerIds: readonly string[];
  readonly since: string | null;
  readonly params: ProblemParams;
  // 2026-09-28 (D-243) — the incident and the operator's marks (absent / null
  // from an older server; normalizeAlarms always fills them).
  /** null = no incident row (the table is not available): no marks possible. */
  readonly incidentId?: string | null;
  readonly openedAt?: string | null;
  readonly acknowledgedAt?: string | null;
  readonly acknowledgedBy?: string | null;
  readonly reviewedAt?: string | null;
  readonly reviewedBy?: string | null;
  readonly reviewNote?: string | null;
  /** Only while in the future. */
  readonly snoozedUntil?: string | null;
  readonly snoozedBy?: string | null;
  /** An ALARM e-mail carried it. */
  readonly emailed?: boolean;
}

export type IncidentStoreStatus = 'ok' | 'unavailable' | 'disabled' | 'unknown';

export interface OverviewAlarmsResponse {
  readonly evaluatedAt: string | null;
  readonly evaluatorAlive: boolean;
  readonly alarms: readonly OverviewAlarm[];
  readonly notifier: {
    readonly emailConfigured: boolean;
    readonly lastSentAt: string | null;
    /** Additive (2026-09-28): alarm e-mails in the last 24 h and the daily cap; null from an older server. */
    readonly emailsSent24h?: number | null;
    readonly maxEmailsPerDay?: number | null;
  };
  /** Additive (2026-09-28): whether Okundu / İncelendi / Ertele can be stored ('unknown' from an older server). */
  readonly incidentStore?: IncidentStoreStatus;
}

/** 2026-09-28 (D-243) — the incident as the mark routes return it. */
export interface OverviewIncident {
  readonly incidentId: string;
  readonly alarmId: string;
  readonly code: string;
  readonly severity: 'critical' | 'warn';
  readonly providerIds: readonly string[];
  readonly params: ProblemParams;
  readonly openedAt: string | null;
  readonly resolvedAt: string | null;
  readonly acknowledgedAt: string | null;
  readonly acknowledgedBy: string | null;
  readonly reviewedAt: string | null;
  readonly reviewedBy: string | null;
  readonly reviewNote: string | null;
  readonly snoozedUntil: string | null;
  readonly snoozedBy: string | null;
}

export type IncidentAction = 'acknowledge' | 'review' | 'snooze';

export interface IncidentMarkResult {
  readonly action: IncidentAction;
  readonly changed: boolean;
  readonly incident: OverviewIncident;
}

/** The snooze choices (server: 60 / 240 / 1440 minutes). */
export const SNOOZE_CHOICES: ReadonlyArray<{ readonly minutes: 60 | 240 | 1440; readonly label: string }> = [
  { minutes: 60, label: '1 saat' },
  { minutes: 240, label: '4 saat' },
  { minutes: 1440, label: '24 saat' },
];
/** Server CHECK: a review note is at most 500 characters. */
export const REVIEW_NOTE_MAX = 500;

export interface RetryProblematicDryRun {
  readonly dryRun: true;
  readonly candidates: number;
  readonly sticky: number;
  readonly skippedInFlight: number;
  readonly noCredential: number;
}

export interface RetryProblematicApplied {
  readonly dryRun: false;
  readonly requested: number;
  readonly nudged: number;
  readonly skippedInFlight: number;
}

export type RetryProblematicResult = RetryProblematicDryRun | RetryProblematicApplied;

export interface RetryProblematicInput {
  readonly confirm: boolean;
  readonly attempts?: 1 | 2 | 3;
  readonly reason?: string;
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

export const OVERVIEW_PATH = '/admin/overview';
export const OVERVIEW_ALARMS_PATH = '/admin/overview/alarms';
export const OVERVIEW_INCIDENTS_PATH = '/admin/overview/incidents';

export async function fetchOverview(options: ApiClientOptions): Promise<ApiResult<OverviewResponse>> {
  const res = await callAdminApi<unknown>(options, { path: OVERVIEW_PATH });
  // 503 {error:'overview_unavailable'} → a specific, translatable reason.
  if (res.kind !== 'ok') return liftServerCode(res);
  const data = normalizeOverview(res.data);
  return data
    ? { kind: 'ok', status: res.status, data }
    : { kind: 'error', status: res.status, reason: 'invalid_overview_payload', body: null };
}

export async function fetchOverviewAlarms(
  options: ApiClientOptions,
): Promise<ApiResult<OverviewAlarmsResponse>> {
  const res = await callAdminApi<unknown>(options, { path: OVERVIEW_ALARMS_PATH });
  if (res.kind !== 'ok') return res;
  const data = normalizeAlarms(res.data);
  return data
    ? { kind: 'ok', status: res.status, data }
    : { kind: 'error', status: res.status, reason: 'invalid_alarms_payload', body: null };
}

/**
 * 2026-09-28 (D-243) — Okundu (acknowledge), İncelendi (review + optional
 * note), Ertele (snooze 60 / 240 / 1440 min). Audited server-side
 * (provider:inspect). Acknowledge sends no body (no Content-Type).
 */
export async function markIncident(
  options: ApiClientOptions,
  incidentId: string,
  action: IncidentAction,
  input: { readonly note?: string | null; readonly minutes?: number } = {},
): Promise<ApiResult<IncidentMarkResult>> {
  const body =
    action === 'review'
      ? { note: input.note ?? null }
      : action === 'snooze'
        ? { minutes: input.minutes }
        : undefined;
  const res = await callAdminApi<unknown>(options, {
    method: 'POST',
    path: `${OVERVIEW_INCIDENTS_PATH}/${encodeURIComponent(incidentId)}/${action}`,
    ...(body !== undefined ? { body } : {}),
  });
  if (res.kind !== 'ok') return liftServerCode(res);
  const data = normalizeMarkResult(res.data, action);
  return data
    ? { kind: 'ok', status: res.status, data }
    : { kind: 'error', status: res.status, reason: 'invalid_incident_payload', body: null };
}

export async function retryProblematic(
  options: ApiClientOptions,
  providerId: string,
  input: RetryProblematicInput,
): Promise<ApiResult<RetryProblematicResult>> {
  const body: Record<string, unknown> = { confirm: input.confirm === true };
  if (input.attempts !== undefined) body.attempts = input.attempts;
  if (input.reason !== undefined && input.reason.length > 0) body.reason = input.reason;
  const res = await callAdminApi<unknown>(options, {
    method: 'POST',
    path: `/admin/providers/${encodeURIComponent(providerId)}/parents/retry-problematic`,
    body,
  });
  if (res.kind !== 'ok') return liftServerCode(res);
  const data = normalizeRetryResult(res.data);
  return data
    ? { kind: 'ok', status: res.status, data }
    : { kind: 'error', status: res.status, reason: 'invalid_retry_payload', body: null };
}

/**
 * The shared client only lifts `body.reason`; the provider routes answer
 * `{error, detail}` (e.g. 409 {error:'conflict', detail:'no_credential_on_account'}).
 * Prefer the specific `detail`, then `error`, so the UI can map it to Turkish.
 */
export function liftServerCode<T>(res: ApiResult<T>): ApiResult<T> {
  if (res.kind !== 'error' || !res.reason.startsWith('http_')) return res;
  const detail = res.body?.detail;
  if (typeof detail === 'string' && detail.length > 0) return { ...res, reason: detail };
  const code = res.body?.error;
  if (typeof code === 'string' && code.length > 0) return { ...res, reason: code };
  return res;
}

// ---------------------------------------------------------------------------
// Normalisers (pure — unit-tested through the page tests)
// ---------------------------------------------------------------------------

type Rec = Record<string, unknown>;

function isRec(v: unknown): v is Rec {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function num(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}
function numOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}
function strOrNull(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}
function boolOrNull(v: unknown): boolean | null {
  return typeof v === 'boolean' ? v : null;
}
function rec(v: unknown): Rec {
  return isRec(v) ? v : {};
}
function arr(v: unknown): readonly unknown[] {
  return Array.isArray(v) ? v : [];
}
function oneOf<T extends string>(v: unknown, allowed: readonly T[], fallback: T): T {
  return typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;
}
function params(v: unknown): ProblemParams {
  const out: Record<string, string | number | null> = {};
  for (const [k, val] of Object.entries(rec(v))) {
    if (typeof val === 'string' || (typeof val === 'number' && Number.isFinite(val)) || val === null) {
      out[k] = val;
    }
  }
  return out;
}

const SEVERITIES: readonly OverviewSeverity[] = ['critical', 'warn', 'info'];

/** 2026-09-23 — the additive per site × family split (tolerates a missing / partial field). */
export function normalizeSiteFamilyDelivery(v: unknown): readonly OverviewSiteFamilyDelivery[] {
  return arr(v)
    .filter(isRec)
    .map((f) => ({
      site: str(f.site),
      providerId: str(f.providerId),
      finished: num(f.finished),
      delivered: num(f.delivered),
      ratePct: num(f.ratePct),
      domains: arr(f.domains)
        .filter(isRec)
        .map((d) => ({
          domain: str(d.domain),
          finished: num(d.finished),
          delivered: num(d.delivered),
          ratePct: num(d.ratePct),
          finished1h: num(d.finished1h),
          delivered1h: num(d.delivered1h),
        }))
        .filter((d) => d.domain.length > 0),
    }))
    .filter((f) => f.site.length > 0 && f.providerId.length > 0);
}
const PROVIDER_STATUSES: readonly ProviderHealthStatus[] = ['ok', 'warn', 'critical', 'idle'];
const GATEWAY_STATUSES: readonly GatewayHealthStatus[] = ['ok', 'down', 'unknown'];

function normalizeReasons(v: unknown): readonly OverviewReasonCount[] {
  return arr(v)
    .filter(isRec)
    .map((r) => ({ code: str(r.code, 'unknown'), count: num(r.count) }))
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count);
}

function normalizeProvider(raw: Rec): OverviewProvider | null {
  const providerId = strOrNull(raw.providerId);
  if (!providerId) return null;
  const a = rec(raw.accounts);
  const s = rec(raw.stock);
  const o24 = rec(raw.orders24h);
  const o7 = rec(raw.orders7d);
  const renewal = isRec(raw.renewal24h)
    ? { completed: num(raw.renewal24h.completed), failed: num(raw.renewal24h.failed) }
    : null;
  const gateway = isRec(raw.gateway)
    ? { status: oneOf(raw.gateway.status, GATEWAY_STATUSES, 'unknown'), since: strOrNull(raw.gateway.since) }
    : null;
  return {
    providerId,
    label: str(raw.label, providerId),
    kind: raw.kind === 'alias' ? 'alias' : 'mailbox',
    onSale: raw.onSale === true,
    activeDomains: num(raw.activeDomains),
    accounts: {
      total: num(a.total),
      active: num(a.active),
      verified: num(a.verified),
      pending: num(a.pending),
      loginFailed: num(a.loginFailed),
      sticky: num(a.sticky),
      disabled: num(a.disabled),
      suspended: num(a.suspended),
      noCredential: num(a.noCredential),
      lastVerifiedAt: strOrNull(a.lastVerifiedAt),
    },
    quarantineReasons: normalizeReasons(raw.quarantineReasons),
    stock: {
      displayed: num(s.displayed),
      sellable: num(s.sellable),
      rawAvailable: num(s.rawAvailable),
      inFlight: num(s.inFlight),
    },
    orders24h: { total: num(o24.total), delivered: num(o24.delivered), cancelled: num(o24.cancelled) },
    orders7d: { total: num(o7.total), delivered: num(o7.delivered) },
    lastDeliveryAt: strOrNull(raw.lastDeliveryAt),
    renewal24h: renewal,
    gateway,
    status: oneOf(raw.status, PROVIDER_STATUSES, 'idle'),
  };
}

function normalizeAction(v: unknown): OverviewProblemAction {
  if (!isRec(v)) return null;
  if (v.kind === 'retry_problematic' && typeof v.providerId === 'string' && v.providerId.length > 0) {
    return { kind: 'retry_problematic', providerId: v.providerId };
  }
  if (v.kind === 'link' && typeof v.href === 'string' && v.href.length > 0) {
    return { kind: 'link', href: v.href };
  }
  return null;
}

export function normalizeOverview(raw: unknown): OverviewResponse | null {
  if (!isRec(raw) || typeof raw.generatedAt !== 'string' || !Array.isArray(raw.providers)) return null;
  const sys = rec(raw.system);
  const o24 = rec(sys.orders24h);
  const stuck = rec(sys.stuckOrders);
  const residential = rec(sys.residential);
  return {
    generatedAt: raw.generatedAt,
    cacheAgeSec: num(raw.cacheAgeSec),
    system: {
      orders24h: {
        total: num(o24.total),
        delivered: num(o24.delivered),
        cancelled: num(o24.cancelled),
        open: num(o24.open),
      },
      ordersDaily7d: arr(sys.ordersDaily7d)
        .filter(isRec)
        .map((d) => ({
          day: str(d.day),
          total: num(d.total),
          delivered: num(d.delivered),
          cancelled: num(d.cancelled),
        }))
        .sort((x, y) => (x.day < y.day ? -1 : x.day > y.day ? 1 : 0)),
      stuckOrders: { expiredLease: num(stuck.expiredLease), oldReserved: num(stuck.oldReserved) },
      residential: {
        configured: boolOrNull(residential.configured),
        host: typeof residential.host === 'string' ? residential.host : null,
        port: numOrNull(residential.port),
        status: oneOf(residential.status, ['ok', 'down', 'unknown', 'unconfigured'] as const, 'unknown'),
      },
      runtime: { metricsFresh: boolOrNull(rec(sys.runtime).metricsFresh) },
      notifier: { emailConfigured: rec(sys.notifier).emailConfigured === true },
      metricsAvailable: sys.metricsAvailable === true,
      deliveryBySiteFamily24h: normalizeSiteFamilyDelivery(sys.deliveryBySiteFamily24h),
      stockByDomain: normalizeDomainStock(sys.stockByDomain),
      // An older server has no field at all: unknown (never "no low stock").
      stockByDomainKnown: sys.stockByDomainKnown === true && Array.isArray(sys.stockByDomain),
      stockDomainsWithDemand: num(sys.stockDomainsWithDemand),
      stockIdleLowCount: num(sys.stockIdleLowCount),
    },
    gateways: arr(raw.gateways)
      .filter(isRec)
      .map((g) => ({
        providerIds: arr(g.providerIds).filter((p): p is string => typeof p === 'string'),
        host: str(g.host),
        port: num(g.port),
        status: oneOf(g.status, GATEWAY_STATUSES, 'unknown'),
        since: strOrNull(g.since),
        lastOkAt: strOrNull(g.lastOkAt),
        source: oneOf(g.source, ['canary', 'probes', 'none'] as const, 'none'),
      })),
    providers: raw.providers
      .filter(isRec)
      .map(normalizeProvider)
      .filter((p): p is OverviewProvider => p !== null),
    problems: arr(raw.problems)
      .filter(isRec)
      .map((p, i) => ({
        id: str(p.id, `problem-${i}`),
        severity: oneOf(p.severity, SEVERITIES, 'warn'),
        code: str(p.code, 'unknown'),
        providerId: strOrNull(p.providerId),
        params: params(p.params),
        since: strOrNull(p.since),
        action: normalizeAction(p.action),
      })),
  };
}

export function normalizeAlarms(raw: unknown): OverviewAlarmsResponse | null {
  if (!isRec(raw) || !Array.isArray(raw.alarms) || typeof raw.evaluatorAlive !== 'boolean') return null;
  const notifier = rec(raw.notifier);
  return {
    evaluatedAt: strOrNull(raw.evaluatedAt),
    evaluatorAlive: raw.evaluatorAlive,
    alarms: raw.alarms
      .filter(isRec)
      .filter((a) => a.severity === 'critical' || a.severity === 'warn')
      .map((a, i) => ({
        id: str(a.id, `alarm-${i}`),
        severity: a.severity === 'critical' ? ('critical' as const) : ('warn' as const),
        code: str(a.code, 'unknown'),
        providerIds: arr(a.providerIds).filter((p): p is string => typeof p === 'string'),
        since: strOrNull(a.since),
        params: params(a.params),
        incidentId: strOrNull(a.incidentId),
        openedAt: strOrNull(a.openedAt),
        acknowledgedAt: strOrNull(a.acknowledgedAt),
        acknowledgedBy: strOrNull(a.acknowledgedBy),
        reviewedAt: strOrNull(a.reviewedAt),
        reviewedBy: strOrNull(a.reviewedBy),
        reviewNote: strOrNull(a.reviewNote),
        snoozedUntil: strOrNull(a.snoozedUntil),
        snoozedBy: strOrNull(a.snoozedBy),
        emailed: a.emailed === true,
      })),
    notifier: {
      emailConfigured: notifier.emailConfigured === true,
      lastSentAt: strOrNull(notifier.lastSentAt),
      emailsSent24h: numOrNull(notifier.emailsSent24h),
      maxEmailsPerDay: numOrNull(notifier.maxEmailsPerDay),
    },
    incidentStore: oneOf<IncidentStoreStatus>(raw.incidentStore, ['ok', 'unavailable', 'disabled'], 'unknown'),
  };
}

/** 2026-09-28 — system.stockByDomain (tolerates a missing / partial field). */
export function normalizeDomainStock(v: unknown): readonly OverviewDomainStock[] {
  return arr(v)
    .filter(isRec)
    .map((d) => ({
      providerId: str(d.providerId),
      domain: str(d.domain),
      reservable: num(d.reservable),
      orders24h: num(d.orders24h),
      peakHour24h: num(d.peakHour24h),
      coverHours: numOrNull(d.coverHours),
      status: d.status === 'out' ? ('out' as const) : ('low' as const),
    }))
    .filter((d) => d.providerId.length > 0 && d.domain.length > 0);
}

export function normalizeIncident(raw: unknown): OverviewIncident | null {
  if (!isRec(raw) || typeof raw.incidentId !== 'string' || raw.incidentId.length === 0) return null;
  return {
    incidentId: raw.incidentId,
    alarmId: str(raw.alarmId),
    code: str(raw.code, 'unknown'),
    severity: raw.severity === 'critical' ? 'critical' : 'warn',
    providerIds: arr(raw.providerIds).filter((p): p is string => typeof p === 'string'),
    params: params(raw.params),
    openedAt: strOrNull(raw.openedAt),
    resolvedAt: strOrNull(raw.resolvedAt),
    acknowledgedAt: strOrNull(raw.acknowledgedAt),
    acknowledgedBy: strOrNull(raw.acknowledgedBy),
    reviewedAt: strOrNull(raw.reviewedAt),
    reviewedBy: strOrNull(raw.reviewedBy),
    reviewNote: strOrNull(raw.reviewNote),
    snoozedUntil: strOrNull(raw.snoozedUntil),
    snoozedBy: strOrNull(raw.snoozedBy),
  };
}

function normalizeMarkResult(raw: unknown, action: IncidentAction): IncidentMarkResult | null {
  if (!isRec(raw)) return null;
  const incident = normalizeIncident(raw.incident);
  if (!incident) return null;
  return { action, changed: raw.changed === true, incident };
}

export function normalizeRetryResult(raw: unknown): RetryProblematicResult | null {
  if (!isRec(raw) || typeof raw.dryRun !== 'boolean') return null;
  if (raw.dryRun) {
    return {
      dryRun: true,
      candidates: num(raw.candidates),
      sticky: num(raw.sticky),
      skippedInFlight: num(raw.skippedInFlight),
      noCredential: num(raw.noCredential),
    };
  }
  return {
    dryRun: false,
    requested: num(raw.requested),
    nudged: num(raw.nudged),
    skippedInFlight: num(raw.skippedInFlight),
  };
}
